import React, { useEffect, useMemo, useState } from "react";
import CountryHero from "@/components/country/CountryHero";
import CountryByCity from "@/components/country/CountryByCity";
import CTAStrip from "@/components/blocks/CTAStrip";
import { fetchExperiences } from "@/lib/api";
import { useLang, COUNTRY_LABELS, CITY_LABELS } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";

const COUNTRY_META = {
  spain: {
    image: "/static/experiences/donana-horseback/hero.png",
    image_alt: "Doñana wetlands at golden hour",
    cities: ["madrid", "sevilla"],
    city_image: {
      madrid: "/static/experiences/hike-guadarrama-escorial/hero.png",
      sevilla: "/static/experiences/donana-horseback/hero.png",
    },
    manifesto: {
      en: [
        "Spain is the studio's first country and its most familiar. We know the Sierra de Guadarrama in May and the Andalusian marsh in October as well as we know our own kitchens.",
        "Eight experiences across two cities: the Madrid hinterland — royal sites, granite mountains, a master ceramist — and the Sevillian south, where Doñaña's pinewoods, Aracena's red earth and a Sevillian espadrille atelier compose the second half of the dossier.",
      ],
      es: [
        "España es el primer país del estudio y el más familiar. Conocemos la Sierra de Guadarrama en mayo y la marisma andaluza en octubre tan bien como nuestras propias cocinas.",
        "Ocho experiencias entre dos ciudades: el campo de Madrid —reales sitios, sierra de granito, un maestro ceramista— y el sur sevillano, donde el pinar de Doñaña, la tierra roja de Aracena y un taller de alpargatas sevillano componen la segunda mitad del dossier.",
      ],
    },
    title_italic_tail: { en: "refined.", es: "con calma." },
  },
  italy: {
    image: "/static/experiences/albano-lake-kayak/hero.png",
    image_alt: "Lake Albano volcanic crater",
    cities: ["roma", "campania"],
    city_image: {
      roma: "/static/experiences/agrippa-baths-candlelight/hero.png",
      campania: "/static/experiences/sup-queen-giovanna-baths/hero.jpg",
    },
    manifesto: {
      en: [
        "Italy is where the studio sends travelers who want to be told something they don't already know about Rome. Seven experiences arranged on two coordinates: the Castelli Romani above the capital, and the Bay of Naples below.",
        "A candlelit dinner inside the Baths of Agrippa, a kayak across a volcanic lake, a private bedroom of Caravaggio. South of Rome, the Sanità district by foot and a glove cut to your hand. The order in which we send them matters.",
      ],
      es: [
        "Italia es adónde el estudio envía a quien quiere que le cuenten algo que no sepa sobre Roma. Siete experiencias en dos coordenadas: los Castelli Romani sobre la capital y el Golfo de Nápoles debajo.",
        "Una cena a la luz de las velas en los Baños de Agripa, un kayak en un lago volcánico, una habitación privada de Caravaggio. Al sur de Roma, el barrio de Sanità a pie y un guante hecho a la medida de la mano. El orden en que los enviamos importa.",
      ],
    },
    title_italic_tail: { en: "considered.", es: "con cuidado." },
  },
  portugal: {
    image: "/static/experiences/arrabida-ebike-wine/hero.png",
    image_alt: "Arrábida coastline from above",
    cities: ["lisboa"],
    city_image: { lisboa: "/static/experiences/arrabida-ebike-wine/hero.png" },
    manifesto: {
      en: [
        "Portugal is where the studio sends travelers who want the Atlantic but not the cliffs of Cliffs of Açores; the wine but not the Douro circuit; Sintra but on a slower clock.",
        "Five afternoons on either side of Lisbon, each one composed in person over the past decade: a family vineyard near Sintra, an e-bike along Arrábida, a Roman-inspired lunch in Setúbal, a Bucelas horseback hour, a classic-car wine afternoon north of the city.",
      ],
      es: [
        "Portugal es adónde el estudio envía a quien quiere el Atlántico pero no los acantilados azoreanos; el vino pero no el circuito del Duero; Sintra pero a otro reloj.",
        "Cinco tardes a un lado y otro de Lisboa, compuestas en persona durante la última década: un viñedo familiar junto a Sintra, una e-bike por Arrábida, un almuerzo de inspiración romana en Setúbal, una hora a caballo en Bucelas y una tarde de vinos en coche clásico al norte.",
      ],
    },
    title_italic_tail: { en: "on its clock.", es: "a su ritmo." },
  },
};

/**
 * Per-country landing page. Loads experiences for one country code, groups
 * them by city, and renders three editorial blocks:
 *
 *   <CountryHero />     hero + manifesto
 *   <CountryByCity />   alternating city portraits + experience grids
 *   <CTAStrip />        final call-to-action
 */
export default function CountryPage({ code }) {
  const { lang, t } = useLang();
  const [experiences, setExperiences] = useState([]);

  const meta = COUNTRY_META[code];
  const countryName = COUNTRY_LABELS[code]?.[lang] || code;

  useSeo({
    title: `${countryName} — Viaja de Verdad`,
    description: (meta?.manifesto?.[lang] || [])[0] || "",
    image: meta?.image,
    locale: lang === "es" ? "es_ES" : "en_US",
    type: "article",
  });

  useEffect(() => {
    let cancelled = false;
    fetchExperiences({ country: code, lang })
      .then((data) => {
        if (!cancelled) setExperiences(data);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.error("[VdV][CountryPage] fetch failed:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, lang]);

  const byCity = useMemo(() => {
    const groups = {};
    for (const e of experiences) {
      (groups[e.city] = groups[e.city] || []).push(e);
    }
    return groups;
  }, [experiences]);

  return (
    <div data-testid={`country-page-${code}`} className="bg-ivory">
      <CountryHero
        meta={meta}
        countryName={countryName}
        italicTail={meta.title_italic_tail[lang]}
        experiencesCount={experiences.length}
        countLabel={t.collection.count_label}
        eyebrow={t.country.eyebrow}
        manifestoLines={meta.manifesto[lang]}
      />

      <CountryByCity
        meta={meta}
        cityLabels={CITY_LABELS}
        byCity={byCity}
        lang={lang}
        eyebrow={t.country.by_city_eyebrow}
        countLabel={t.collection.count_label}
      />

      <CTAStrip
        eyebrow={t.country.cta_eyebrow}
        headline={`${t.country.cta_main_pre} ${countryName}${t.country.cta_main_post}`}
        italicTail={t.country.cta_main_italic}
        subtitle={t.country.cta_sub}
        buttonLabel={t.country.cta_button}
        buttonTo={`/experiences?country=${code}`}
        image={meta.image}
        imageAlt={meta.image_alt}
      />
    </div>
  );
}
